import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../contexts/UserAuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import TimezoneSelector from '../../components/common/TimezoneSelector';
import { formatDate as formatDateTz } from '../../utils/timezoneUtils';

interface DashboardStats {
  total_requests: number;
  completed_requests: number;
  countries_worked: Array<{ country: string; flag: string; count: number }>;
}

interface ProfileData {
  user: {
    id: number;
    name: string;
    email: string;
    whatsapp: string;
    email_verified: boolean;
    preferred_language: string;
    created_at: string;
  };
  stats: DashboardStats;
}

interface NotificationSettings {
  email_notifications: boolean;
  whatsapp_notifications: boolean;
  request_updates: boolean;
  marketing_emails: boolean;
}

const Profile: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser } = useUserAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    whatsapp: '',
    preferred_language: 'en'
  });
  
  const [notifications, setNotifications] = useState<NotificationSettings>({
    email_notifications: true,
    whatsapp_notifications: true,
    request_updates: true,
    marketing_emails: false
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/backend/api/user/profile.php', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setProfileData(result.data);
        setFormData({
          name: result.data.user.name,
          whatsapp: result.data.user.whatsapp,
          preferred_language: result.data.user.preferred_language || 'en'
        });
        if (result.data.preferences) {
          setNotifications(result.data.preferences);
        }
        setError(null);
      } else {
        setError(result.error || 'Failed to fetch profile data');
      }
    } catch (error) {      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await fetch('/backend/api/user/profile.php', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          whatsapp: formData.whatsapp,
          preferred_language: formData.preferred_language
        })
      });

      const result = await response.json();
      
      if (result.success) {
        updateUser({
          name: formData.name,
          whatsapp: formData.whatsapp,
          preferred_language: formData.preferred_language
        });
        setSuccess(t('profile.updated'));
        setEditMode(false);
        
        // Update language if changed
        if (formData.preferred_language !== i18n.language) {
          i18n.changeLanguage(formData.preferred_language);
        }
      } else {
        setError(result.error || t('profile.error'));
      }
    } catch (error) {      setError(t('profile.error'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError(t('profile.passwordMismatch'));
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const response = await fetch('/backend/api/user/password.php', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(t('profile.passwordChanged'));
        setShowPasswordForm(false);
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        setError(result.error || t('profile.passwordError'));
      }
    } catch (error) {      setError(t('profile.passwordError'));
    } finally {
      setSaving(false);
    }
  };

  const formatDateDisplay = (dateString: string) => {
    const locale = i18n.language === 'ar' ? 'ar' : 'en-US';
    return formatDateTz(dateString, locale);
  };

  // Render country flag using CDN with emoji fallback
  const renderCountryFlag = (countryFlag: string, countryName: string) => {
    // countryFlag can be: country code (e.g., 'US') or emoji (e.g., '🇺🇸')
    const isEmoji = countryFlag && countryFlag.length > 2;
    const fallbackEmoji = isEmoji ? countryFlag : '🌍';
    const countryCode = isEmoji ? '' : countryFlag;

    if (countryCode && countryCode.length <= 3) {
      return (
        <img
          src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
          alt={`${countryName} flag`}
          className="w-4 h-3 object-cover rounded-sm inline"
          onError={(e) => {
            const target = e.currentTarget;
            target.style.display = 'none';
            const parent = target.parentNode as HTMLElement;
            if (parent && !parent.querySelector('.flag-fallback')) {
              const span = document.createElement('span');
              span.className = 'flag-fallback';
              span.textContent = fallbackEmoji;
              parent.appendChild(span);
            }
          }}
        />
      );
    }
    return <span>{fallbackEmoji}</span>;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !profileData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchProfileData}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            {t('profile.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('profile.title')}
        </h1>
        <p className="text-gray-600">
          {t('profile.manageSettings')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 rtl:mr-3 rtl:ml-0">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 rtl:mr-3 rtl:ml-0">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Personal Information */}
        <div className="lg:col-span-2 space-y-8">
          {/* Personal Info Card */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.personalInfo')}
              </h2>
              <button
                onClick={() => editMode ? setEditMode(false) : setEditMode(true)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {editMode ? t('profile.cancel') : t('profile.edit')}
              </button>
            </div>
            
            <div className="p-6">
              {editMode ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.fullName')}
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.whatsappNumber')}
                    </label>
                    <input
                      type="text"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.preferredLanguage')}
                    </label>
                    <select
                      value={formData.preferred_language}
                      onChange={(e) => setFormData({...formData, preferred_language: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="en">English</option>
                      <option value="ar">العربية</option>
                    </select>
                  </div>
                  
                  <div className="flex space-x-4 rtl:space-x-reverse">
                    <button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? t('profile.updating') : t('profile.save')}
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    >
                      {t('profile.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('profile.fullName')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{profileData?.user.name || user?.name}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('profile.emailAddress')}</dt>
                    <dd className="mt-1 flex items-center">
                      <span className="text-sm text-gray-900">{profileData?.user.email || user?.email}</span>
                      <span className={`ml-2 rtl:mr-2 rtl:ml-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (profileData?.user.email_verified || user?.email_verified) 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(profileData?.user.email_verified || user?.email_verified) 
                          ? t('profile.emailVerified') 
                          : t('profile.emailUnverified')}
                      </span>
                    </dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('profile.whatsappNumber')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">{profileData?.user.whatsapp || user?.whatsapp}</dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('profile.preferredLanguage')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {(profileData?.user.preferred_language || user?.preferred_language) === 'ar' ? 'العربية' : 'English'}
                    </dd>
                  </div>
                  
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t('profile.memberSince')}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {profileData?.user.created_at ? formatDateDisplay(profileData.user.created_at) : 'N/A'}
                    </dd>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Security Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.security')}
              </h2>
            </div>

            <div className="p-6">
              {!showPasswordForm ? (
                <button
                  onClick={() => setShowPasswordForm(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  {t('profile.changePassword')}
                </button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.currentPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.newPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('profile.confirmNewPassword')}
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex space-x-4 rtl:space-x-reverse">
                    <button
                      onClick={handlePasswordChange}
                      disabled={saving}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {saving ? t('profile.updating') : t('profile.save')}
                    </button>
                    <button
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                    >
                      {t('profile.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Timezone Preferences Section */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.timezonePreferences', 'Timezone Preferences')}
              </h2>
            </div>

            <div className="p-6">
              <TimezoneSelector />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.quickStats')}
              </h2>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">
                  {profileData?.stats.total_requests || 0}
                </div>
                <div className="text-sm text-gray-500">
                  {t('profile.totalCompanies')}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {profileData?.stats.completed_requests || 0}
                </div>
                <div className="text-sm text-gray-500">
                  {t('profile.completedCompanies')}
                </div>
              </div>
              
              {profileData?.stats.countries_worked && profileData.stats.countries_worked.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t('profile.countriesWorked')}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profileData.stats.countries_worked.map((country, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                      >
                        <span className="mr-1 rtl:ml-1 rtl:mr-0">
                          {renderCountryFlag(country.flag, country.country)}
                        </span>
                        {country.country} ({country.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.quickActions')}
              </h2>
            </div>
            
            <div className="p-6 space-y-3">
              <Link
                to="/dashboard/create-request"
                className="block w-full text-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                {t('profile.startNewRequest')}
              </Link>
              
              <Link
                to="/dashboard/requests"
                className="block w-full text-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('profile.viewAllRequests')}
              </Link>
            </div>
          </div>

          {/* Account Status */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('profile.accountStatus')}
              </h2>
            </div>
            
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="ml-3 rtl:mr-3 rtl:ml-0">
                  <p className="text-sm font-medium text-gray-900">
                    {t('profile.active')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {t('profile.accountGoodStanding')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;